import type { LucideIcon } from "lucide-react";
import { normalizeSvgSrc } from "utils/normalize-svg-src";
import {
  Award,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  CircleDollarSign,
  ClipboardList,
  Coins,
  Compass,
  Download,
  FileBadge,
  Flag,
  Gift,
  GraduationCap,
  Grid3X3,
  HelpCircle,
  ListChecks,
  Mail,
  Map,
  MessageSquare,
  MessagesSquare,
  Radar,
  Settings,
  ShoppingBag,
  Sparkles,
  Store,
  Target,
  Trophy,
  Truck,
  UserCheck,
  Users,
  Wand2,
} from "lucide-react";
import { ICONS_MODULES_PATHS } from "./sidebar-icon-modules-path";

/** Semantic module keys (preferred over SVG paths). */
export const MODULE_ICON_KEYS = {
  evaluations: "evaluations",
  appreciation: "appreciation",
  myEvaluations: "my-evaluations",
  resources: "resources",
  radar: "radar",
  teams: "teams",
  explore: "explore",
  anniversaries: "anniversaries",
  message: "message",
  capsules: "capsules",
  canva: "canva",
  recognition: "recognition",
  challenges: "challenges",
  userChallenges: "user-challenges",
  createChallenges: "create-challenges",
  ranking: "ranking",
  actionPlans: "action-plans",
  myActionPlan: "my-action-plan",
  createActionPlan: "create-action-plan",
  surveys: "surveys",
  survey: "survey",
  mySurvey: "my-survey",
  anonymous: "anonymous",
  performanceEvaluation: "performance-evaluation",
  anonymousIndex: "anonymous-index",
  fileBadge: "file-badge",
  marketplace: "marketplace",
  coins: "coins",
  products: "products",
  productsCategory: "products-category",
  coinsConcept: "coins-concept",
  exchangeControl: "exchange-control",
  activities: "activities",
  career: "career",
  myDevelopmentPlan: "my-development-plan",
  developmentPhases: "development-phases",
  developmentPlans: "development-plans",
  jobyfine: "jobyfine",
  requests: "requests",
  jobApplicants: "job-applicants",
  offers: "offers",
  process: "process",
  finder: "finder",
  jobyfinePreselection: "jobyfine-preselection",
  building: "building",
  clipboard: "clipboard",
  config: "config",
  position: "position",
  division: "division",
  area: "area",
  skills: "skills",
  certifications: "certifications",
  users: "users",
  questions: "questions",
  appSetting: "app-setting",
  corporateValues: "corporate-values",
  fullAccess: "full-access",
  vacations: "vacations",
  anonymousMail: "anonymous-mail",
  goals: "goals",
  linksInEmail: "links-in-email",
  notifications: "notifications",
  loginMicrosoft: "login-microsoft",
  loginLinkedin: "login-linkedin",
  activeDirectory: "active-directory",
  flokzu: "flokzu",
  certificate: "certificate",
  industry: "industry",
  benefits: "benefits",
  superAdmin: "super-admin",
  feedback: "feedback",
  publicChallenge: "public-challenge",
  myDownloads: "my-downloads",
  myTasks: "my-tasks",
  benefitRequests: "benefit-requests",
  benefitsPortal: "benefits-portal",
} as const;

export type ModuleIconKey =
  (typeof MODULE_ICON_KEYS)[keyof typeof MODULE_ICON_KEYS];

const MODULE_LUCIDE_BY_KEY: Record<string, LucideIcon> = {
  [MODULE_ICON_KEYS.evaluations]: GraduationCap,
  [MODULE_ICON_KEYS.appreciation]: Award,
  [MODULE_ICON_KEYS.myEvaluations]: UserCheck,
  [MODULE_ICON_KEYS.resources]: BookOpen,
  [MODULE_ICON_KEYS.radar]: Radar,
  [MODULE_ICON_KEYS.teams]: Users,
  [MODULE_ICON_KEYS.explore]: Compass,
  [MODULE_ICON_KEYS.anniversaries]: Gift,
  [MODULE_ICON_KEYS.message]: MessageSquare,
  [MODULE_ICON_KEYS.capsules]: Sparkles,
  [MODULE_ICON_KEYS.canva]: Wand2,
  [MODULE_ICON_KEYS.recognition]: Award,
  [MODULE_ICON_KEYS.challenges]: Trophy,
  [MODULE_ICON_KEYS.userChallenges]: Target,
  [MODULE_ICON_KEYS.createChallenges]: Flag,
  [MODULE_ICON_KEYS.ranking]: BarChart3,
  [MODULE_ICON_KEYS.actionPlans]: ListChecks,
  [MODULE_ICON_KEYS.myActionPlan]: ClipboardList,
  [MODULE_ICON_KEYS.createActionPlan]: ListChecks,
  [MODULE_ICON_KEYS.surveys]: ClipboardList,
  [MODULE_ICON_KEYS.survey]: ClipboardList,
  [MODULE_ICON_KEYS.mySurvey]: ClipboardList,
  [MODULE_ICON_KEYS.anonymous]: HelpCircle,
  [MODULE_ICON_KEYS.performanceEvaluation]: UserCheck,
  [MODULE_ICON_KEYS.anonymousIndex]: HelpCircle,
  [MODULE_ICON_KEYS.fileBadge]: FileBadge,
  [MODULE_ICON_KEYS.marketplace]: Store,
  [MODULE_ICON_KEYS.coins]: Coins,
  [MODULE_ICON_KEYS.products]: ShoppingBag,
  [MODULE_ICON_KEYS.productsCategory]: Grid3X3,
  [MODULE_ICON_KEYS.coinsConcept]: CircleDollarSign,
  [MODULE_ICON_KEYS.exchangeControl]: BarChart3,
  [MODULE_ICON_KEYS.activities]: Target,
  [MODULE_ICON_KEYS.career]: Map,
  [MODULE_ICON_KEYS.myDevelopmentPlan]: Map,
  [MODULE_ICON_KEYS.developmentPhases]: Map,
  [MODULE_ICON_KEYS.developmentPlans]: Map,
  [MODULE_ICON_KEYS.jobyfine]: Briefcase,
  [MODULE_ICON_KEYS.requests]: Briefcase,
  [MODULE_ICON_KEYS.jobApplicants]: Users,
  [MODULE_ICON_KEYS.offers]: Briefcase,
  [MODULE_ICON_KEYS.process]: ListChecks,
  [MODULE_ICON_KEYS.finder]: Radar,
  [MODULE_ICON_KEYS.jobyfinePreselection]: UserCheck,
  [MODULE_ICON_KEYS.building]: Building2,
  [MODULE_ICON_KEYS.clipboard]: ClipboardList,
  [MODULE_ICON_KEYS.config]: Settings,
  [MODULE_ICON_KEYS.position]: Briefcase,
  [MODULE_ICON_KEYS.division]: Building2,
  [MODULE_ICON_KEYS.area]: Grid3X3,
  [MODULE_ICON_KEYS.skills]: Target,
  [MODULE_ICON_KEYS.certifications]: Award,
  [MODULE_ICON_KEYS.users]: Users,
  [MODULE_ICON_KEYS.questions]: HelpCircle,
  [MODULE_ICON_KEYS.appSetting]: Settings,
  [MODULE_ICON_KEYS.corporateValues]: Award,
  [MODULE_ICON_KEYS.fullAccess]: UserCheck,
  [MODULE_ICON_KEYS.vacations]: Calendar,
  [MODULE_ICON_KEYS.anonymousMail]: Mail,
  [MODULE_ICON_KEYS.goals]: Target,
  [MODULE_ICON_KEYS.linksInEmail]: Mail,
  [MODULE_ICON_KEYS.notifications]: MessagesSquare,
  [MODULE_ICON_KEYS.loginMicrosoft]: Building2,
  [MODULE_ICON_KEYS.loginLinkedin]: Users,
  [MODULE_ICON_KEYS.activeDirectory]: Users,
  [MODULE_ICON_KEYS.flokzu]: ListChecks,
  [MODULE_ICON_KEYS.certificate]: Award,
  [MODULE_ICON_KEYS.industry]: Truck,
  [MODULE_ICON_KEYS.benefits]: Gift,
  [MODULE_ICON_KEYS.superAdmin]: Settings,
  [MODULE_ICON_KEYS.feedback]: MessagesSquare,
  [MODULE_ICON_KEYS.publicChallenge]: Trophy,
  [MODULE_ICON_KEYS.myDownloads]: Download,
  [MODULE_ICON_KEYS.myTasks]: ListChecks,
  [MODULE_ICON_KEYS.benefitRequests]: Gift,
  [MODULE_ICON_KEYS.benefitsPortal]: Gift,
};

const MODULE_LUCIDE_BY_PATH: Record<string, LucideIcon> = {
  [ICONS_MODULES_PATHS.LEARNING.EVALUATIONS_MODULE]: GraduationCap,
  [ICONS_MODULES_PATHS.LEARNING.APPRECIATION]: Award,
  [ICONS_MODULES_PATHS.LEARNING.MY_EVALUATIONS]: UserCheck,
  [ICONS_MODULES_PATHS.LEARNING.RESOURCES]: BookOpen,
  [ICONS_MODULES_PATHS.RADAR.RADAR_MODULE]: Radar,
  [ICONS_MODULES_PATHS.RADAR.TEAMS]: Users,
  [ICONS_MODULES_PATHS.EXPLORE.EXPLORE_MODULE]: Compass,
  [ICONS_MODULES_PATHS.EXPLORE.ANNIVERSARIES_MODULE]: Gift,
  [ICONS_MODULES_PATHS.EXPLORE.MESSAGE]: MessageSquare,
  [ICONS_MODULES_PATHS.EXPLORE.CAPSULES]: Sparkles,
  [ICONS_MODULES_PATHS.EXPLORE.CANVA]: Wand2,
  [ICONS_MODULES_PATHS.EXPLORE.RECOGNITION]: Award,
  [ICONS_MODULES_PATHS.CHALLENGES.CHALLENGE_GROUP]: Trophy,
  [ICONS_MODULES_PATHS.CHALLENGES.USER_CHALLENGES]: Target,
  [ICONS_MODULES_PATHS.CHALLENGES.CREATE_CHALLENGES]: Flag,
  [ICONS_MODULES_PATHS.CHALLENGES.RANKING]: BarChart3,
  [ICONS_MODULES_PATHS.ACTION_PLANS.ACTION_PLAN_MODULE]: ListChecks,
  [ICONS_MODULES_PATHS.ACTION_PLANS.ACTION_PLAN_GROUP]: ListChecks,
  [ICONS_MODULES_PATHS.ACTION_PLANS.MY_ACTION_PLAN]: ClipboardList,
  [ICONS_MODULES_PATHS.ACTION_PLANS.CREATE_ACTION_PLAN]: ListChecks,
  [ICONS_MODULES_PATHS.SURVEYS.SURVEY_MODULE]: ClipboardList,
  [ICONS_MODULES_PATHS.SURVEYS.SURVEY_GROUP]: ClipboardList,
  [ICONS_MODULES_PATHS.SURVEYS.SURVEY]: ClipboardList,
  [ICONS_MODULES_PATHS.SURVEYS.MY_SURVEY]: ClipboardList,
  [ICONS_MODULES_PATHS.SURVEYS.ANONYMOUS]: HelpCircle,
  [ICONS_MODULES_PATHS.SURVEYS.PERFORMANCE_EVALUATION]: UserCheck,
  [ICONS_MODULES_PATHS.SURVEYS.ANONYMOUS_INDEX]: HelpCircle,
  [ICONS_MODULES_PATHS.SURVEYS.LIST_PERFORMANCE_EVALUATION]: FileBadge,
  [ICONS_MODULES_PATHS.MARKETPLACE.MARKETPLACE_GROUP]: Store,
  [ICONS_MODULES_PATHS.MARKETPLACE.COINS_MODULE]: Coins,
  [ICONS_MODULES_PATHS.MARKETPLACE.COINS_GROUP]: Coins,
  [ICONS_MODULES_PATHS.MARKETPLACE.PRODUCTS]: ShoppingBag,
  [ICONS_MODULES_PATHS.MARKETPLACE.PRODUCTS_CATEGORY]: Grid3X3,
  [ICONS_MODULES_PATHS.MARKETPLACE.COINS_CONCEPT]: CircleDollarSign,
  [ICONS_MODULES_PATHS.MARKETPLACE.EXCHANGE_CONTROL]: BarChart3,
  [ICONS_MODULES_PATHS.MARKETPLACE.ACTIVITIES]: Target,
  [ICONS_MODULES_PATHS.CAREER_PLAN.MODULE]: Map,
  [ICONS_MODULES_PATHS.CAREER_PLAN.CAREER_GROUP]: Map,
  [ICONS_MODULES_PATHS.CAREER_PLAN.MY_DEVELOPMENT_PLAN]: Map,
  [ICONS_MODULES_PATHS.CAREER_PLAN.DEVELOPMENT_PHASES]: Map,
  [ICONS_MODULES_PATHS.CAREER_PLAN.DEVELOPMENT_PLANS]: Map,
  [ICONS_MODULES_PATHS.JOBYFINE.JOBYFINE_GROUP]: Briefcase,
  [ICONS_MODULES_PATHS.JOBYFINE.REQUESTS]: Briefcase,
  [ICONS_MODULES_PATHS.JOBYFINE.JOB_APPLICANTS]: Users,
  [ICONS_MODULES_PATHS.JOBYFINE.OFFERS]: Briefcase,
  [ICONS_MODULES_PATHS.JOBYFINE.PROCESS]: ListChecks,
  [ICONS_MODULES_PATHS.JOBYFINE.FINDER]: Radar,
  [ICONS_MODULES_PATHS.JOBYFINE.JOBYFINE_PRESELECTION]: UserCheck,
  [ICONS_MODULES_PATHS.JOBYFINE.BUILDING]: Building2,
  [ICONS_MODULES_PATHS.JOBYFINE.BUILDING2]: Building2,
  [ICONS_MODULES_PATHS.JOBYFINE.CLIPBOARD]: ClipboardList,
  [ICONS_MODULES_PATHS.CONFIG.CONFIG_GROUP]: Settings,
  [ICONS_MODULES_PATHS.CONFIG.POSITION]: Briefcase,
  [ICONS_MODULES_PATHS.CONFIG.DIVISION]: Building2,
  [ICONS_MODULES_PATHS.CONFIG.AREA]: Grid3X3,
  [ICONS_MODULES_PATHS.CONFIG.SKILLS]: Target,
  [ICONS_MODULES_PATHS.CONFIG.CERTIFICATIONS]: Award,
  [ICONS_MODULES_PATHS.CONFIG.USERS]: Users,
  [ICONS_MODULES_PATHS.CONFIG.QUESTIONS]: HelpCircle,
  [ICONS_MODULES_PATHS.CONFIG.RESOURCES]: BookOpen,
  [ICONS_MODULES_PATHS.CONFIG.APP_SETTING]: Settings,
  [ICONS_MODULES_PATHS.CONFIG.CORPORATE_VALUES]: Award,
  [ICONS_MODULES_PATHS.CONFIG.FULL_ACCESS]: UserCheck,
  [ICONS_MODULES_PATHS.CONFIG.VACATIONS]: Calendar,
  [ICONS_MODULES_PATHS.CONFIG.ANONYMOUS_MAIL]: Mail,
  [ICONS_MODULES_PATHS.CONFIG.GOALS]: Target,
  [ICONS_MODULES_PATHS.CONFIG.LINKS_IN_EMAIL]: Mail,
  [ICONS_MODULES_PATHS.CONFIG.NOTIFICATION_ICON]: MessagesSquare,
  [ICONS_MODULES_PATHS.CONFIG.LOGIN_MICROSOFT]: Building2,
  [ICONS_MODULES_PATHS.CONFIG.LOGIN_LINKEDIN]: Users,
  [ICONS_MODULES_PATHS.CONFIG.ACTIVE_DIRECTORY]: Users,
  [ICONS_MODULES_PATHS.CONFIG.FLOKZU]: ListChecks,
  [ICONS_MODULES_PATHS.CONFIG.CERTIFICATE]: Award,
  [ICONS_MODULES_PATHS.CONFIG.PERFORMANCE_EVALUATION]: UserCheck,
  [ICONS_MODULES_PATHS.CONFIG.INDUSTRY]: Truck,
  [ICONS_MODULES_PATHS.CONFIG.BENEFITS]: Gift,
  [ICONS_MODULES_PATHS.SUPER_ADMIN.SUPER_ADMIN_GROUP]: Settings,
  [ICONS_MODULES_PATHS.SUPER_ADMIN.FEEDBACK]: MessagesSquare,
  [ICONS_MODULES_PATHS.SUPER_ADMIN.PUBLIC_CHALLENGE]: Trophy,
  [ICONS_MODULES_PATHS.MY_DOWNLOADS.MY_DOWNLOADS]: Download,
  [ICONS_MODULES_PATHS.MY_TASKS.MY_TASKS]: ListChecks,
  [ICONS_MODULES_PATHS.BENEFITS.BENEFITS_MODULE]: Gift,
  [ICONS_MODULES_PATHS.BENEFITS.BENEFIT_REQUESTS]: Gift,
  [ICONS_MODULES_PATHS.BENEFITS.BENEFITS_PORTAL]: Gift,
};

/** Map legacy SVG path leaf → semantic key (e.g. explore-module.svg → explore). */
const PATH_LEAF_TO_KEY: Record<string, string> = {
  "evaluations-module": MODULE_ICON_KEYS.evaluations,
  appreciation: MODULE_ICON_KEYS.appreciation,
  "my-evaluations": MODULE_ICON_KEYS.myEvaluations,
  resources: MODULE_ICON_KEYS.resources,
  "radar-module": MODULE_ICON_KEYS.radar,
  teams: MODULE_ICON_KEYS.teams,
  "explore-module": MODULE_ICON_KEYS.explore,
  "anniversaries-module": MODULE_ICON_KEYS.anniversaries,
  message: MODULE_ICON_KEYS.message,
  capsules: MODULE_ICON_KEYS.capsules,
  canva: MODULE_ICON_KEYS.canva,
  recognition: MODULE_ICON_KEYS.recognition,
  "challenge-group": MODULE_ICON_KEYS.challenges,
  "user-challenges": MODULE_ICON_KEYS.userChallenges,
  "create-challenges": MODULE_ICON_KEYS.createChallenges,
  ranking: MODULE_ICON_KEYS.ranking,
  "action-plan-module": MODULE_ICON_KEYS.actionPlans,
  "action-plan-group": MODULE_ICON_KEYS.actionPlans,
  "my-action-plan": MODULE_ICON_KEYS.myActionPlan,
  "create-action-plan": MODULE_ICON_KEYS.createActionPlan,
  "survey-module": MODULE_ICON_KEYS.surveys,
  "survey-group": MODULE_ICON_KEYS.surveys,
  survey: MODULE_ICON_KEYS.survey,
  "my-survey": MODULE_ICON_KEYS.mySurvey,
  anonymous: MODULE_ICON_KEYS.anonymous,
  "performance-evaluation": MODULE_ICON_KEYS.performanceEvaluation,
  "anonymous-black": MODULE_ICON_KEYS.anonymousIndex,
  "file-badge": MODULE_ICON_KEYS.fileBadge,
  "marketplace-group": MODULE_ICON_KEYS.marketplace,
  "coins-module": MODULE_ICON_KEYS.coins,
  "coins-group": MODULE_ICON_KEYS.coins,
  products: MODULE_ICON_KEYS.products,
  "products-category": MODULE_ICON_KEYS.productsCategory,
  "coins-concept": MODULE_ICON_KEYS.coinsConcept,
  "exchange-control": MODULE_ICON_KEYS.exchangeControl,
  activities: MODULE_ICON_KEYS.activities,
  "career-plan-module": MODULE_ICON_KEYS.career,
  "career-group": MODULE_ICON_KEYS.career,
  "my-development-plan": MODULE_ICON_KEYS.myDevelopmentPlan,
  "development-phases": MODULE_ICON_KEYS.developmentPhases,
  "development-plans": MODULE_ICON_KEYS.developmentPlans,
  "jobyfine-group": MODULE_ICON_KEYS.jobyfine,
  requests: MODULE_ICON_KEYS.requests,
  "job-applicants": MODULE_ICON_KEYS.jobApplicants,
  offers: MODULE_ICON_KEYS.offers,
  process: MODULE_ICON_KEYS.process,
  finder: MODULE_ICON_KEYS.finder,
  "jobyfine-preselection": MODULE_ICON_KEYS.jobyfinePreselection,
  building: MODULE_ICON_KEYS.building,
  clipboard: MODULE_ICON_KEYS.clipboard,
  "config-group": MODULE_ICON_KEYS.config,
  position: MODULE_ICON_KEYS.position,
  division: MODULE_ICON_KEYS.division,
  area: MODULE_ICON_KEYS.area,
  skills: MODULE_ICON_KEYS.skills,
  certifications: MODULE_ICON_KEYS.certifications,
  users: MODULE_ICON_KEYS.users,
  questions: MODULE_ICON_KEYS.questions,
  "app-setting": MODULE_ICON_KEYS.appSetting,
  "corporate-values": MODULE_ICON_KEYS.corporateValues,
  "full-access": MODULE_ICON_KEYS.fullAccess,
  vacations: MODULE_ICON_KEYS.vacations,
  "anonymous-mail": MODULE_ICON_KEYS.anonymousMail,
  goals: MODULE_ICON_KEYS.goals,
  "links-in-email": MODULE_ICON_KEYS.linksInEmail,
  "notification-icon": MODULE_ICON_KEYS.notifications,
  "login-microsoft": MODULE_ICON_KEYS.loginMicrosoft,
  "login-linkedin": MODULE_ICON_KEYS.loginLinkedin,
  "active-directory": MODULE_ICON_KEYS.activeDirectory,
  flokzu: MODULE_ICON_KEYS.flokzu,
  certificate: MODULE_ICON_KEYS.certificate,
  industry: MODULE_ICON_KEYS.industry,
  "benefits-module": MODULE_ICON_KEYS.benefits,
  "super-admin-group": MODULE_ICON_KEYS.superAdmin,
  feedback: MODULE_ICON_KEYS.feedback,
  "public-challenge": MODULE_ICON_KEYS.publicChallenge,
  "my-downloads": MODULE_ICON_KEYS.myDownloads,
  "my-tasks": MODULE_ICON_KEYS.myTasks,
  "benefit-requests": MODULE_ICON_KEYS.benefitRequests,
  "benefits-portal": MODULE_ICON_KEYS.benefitsPortal,
};

function pathToLeaf(path: string): string {
  const base = path.split("/").pop() ?? "";
  return base.replace(/\.(svg|png)$/i, "");
}

export function resolveModuleLucideIcon(src?: unknown): LucideIcon | null {
  if (typeof src === "string" && MODULE_LUCIDE_BY_KEY[src]) {
    return MODULE_LUCIDE_BY_KEY[src];
  }
  const path = normalizeSvgSrc(src);
  if (!path) return null;
  if (MODULE_LUCIDE_BY_PATH[path]) return MODULE_LUCIDE_BY_PATH[path];
  const leaf = pathToLeaf(path);
  const key = PATH_LEAF_TO_KEY[leaf];
  if (key && MODULE_LUCIDE_BY_KEY[key]) return MODULE_LUCIDE_BY_KEY[key];
  return null;
}

export function resolveModuleKeyFromPath(src?: unknown): string | null {
  const path = normalizeSvgSrc(src);
  if (!path) return null;
  return PATH_LEAF_TO_KEY[pathToLeaf(path)] ?? null;
}

export const MODULE_LUCIDE_FALLBACK: LucideIcon = Compass;
